import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const AET_STATUSES = ['AET', 'PEN', 'FT_PEN', 'AOT'];

interface AETBreakdown {
  ft90Home: number;
  ft90Away: number;
  etHome: number;
  etAway: number;
}

export function useAETBreakdown(
  fixtureId: number,
  statusShort: string | null | undefined,
  homeScore: number | null | undefined,
  awayScore: number | null | undefined,
  homeTeamId?: string | null,
  awayTeamId?: string | null
): AETBreakdown | null {
  const isAET = AET_STATUSES.includes((statusShort || '').toUpperCase());

  const { data } = useQuery({
    queryKey: ['aet-breakdown', fixtureId],
    enabled: isAET && homeScore != null && awayScore != null,
    staleTime: Infinity,
    queryFn: async () => {
      // Get game to find team IDs
      const { data: game } = await supabase
        .from('games')
        .select('home_team_id, away_team_id')
        .eq('fixture_id', fixtureId)
        .single();

      if (!game) return null;

      const { data: events } = await supabase
        .from('game_events')
        .select('event_type, event_detail, team_id, time_elapsed')
        .eq('fixture_id', fixtureId)
        .eq('event_type', 'Goal')
        .lte('time_elapsed', 90);

      if (!events) return null;

      let ft90Home = 0;
      let ft90Away = 0;

      for (const ev of events) {
        const detail = (ev.event_detail || '').toLowerCase();
        if (detail.includes('missed')) continue;

        const isOwnGoal = detail.includes('own goal');
        if (isOwnGoal) {
          if (ev.team_id === game.home_team_id) ft90Away++;
          else ft90Home++;
        } else {
          if (ev.team_id === game.home_team_id) ft90Home++;
          else ft90Away++;
        }
      }

      return { ft90Home, ft90Away };
    },
  });

  if (!isAET || !data || homeScore == null || awayScore == null) return null;

  return {
    ft90Home: data.ft90Home,
    ft90Away: data.ft90Away,
    etHome: homeScore - data.ft90Home,
    etAway: awayScore - data.ft90Away,
  };
}
